import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Switch } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import CustomSelect from '../../../common/CustomSelect';
import { unitsTypes, typesToTemplates } from '../../../../constants/dashboards';
import { staticPermissions } from '../../../../constants/authorization';
import { commonEvents } from '../../../../constants/event';

const StyledHeader = styled.div`
  color: #bcbcbc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f7f6f4;
  height: 50px;
  padding: 9px 15px;
  cursor: move;

  .left-header {
    display: flex;
    flex-direction: row;
    width: calc(100% - 105px);

    .item-name-input {
      width: 143px;
    }

    .type-select {
      margin-left: 15px;
      background-color: #f7f6f4;
    }

    .templates-select {
      margin-left: 15px;
      min-width: 125px;
      background-color: #f7f6f4;
    }
  }

  .right-header {
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.65);

    button.square-icon-button {
      margin-left: 4px;
      border: 1px solid transparent;
      background-color: transparent;
    }
  }
`;

const StyledSwitch = styled(Switch)`
  margin: 0 2px;
  white-space: nowrap;
  .ant-switch-inner .ant-switch-inner-checked {
    font-size: 12px;
  }
  .ant-switch-inner .ant-switch-inner-unchecked {
    font-size: 12px;
  }
  .ant-switch-handle {
    top: 2px;
    left: 2px;
  }
  .ant-switch {
    min-width: 44px;
    height: 22px;
    line-height: 22px;
  }
`;

const existsEventsByTab = {
  RETENTION: (eventNames) =>
    !!eventNames.includes(commonEvents.DAU) &&
    !!eventNames.includes(commonEvents.REGISTER_FIRST_START) &&
    !!eventNames.includes(commonEvents.WAU) &&
    !!eventNames.includes(commonEvents.MAU),
  REVENUE: (eventNames) =>
    !!eventNames.includes(commonEvents.PAYMENT) && !!eventNames.includes(commonEvents.DAU),
};

const filterByExistEvents = (type, eventNames) =>
  existsEventsByTab[type] ? existsEventsByTab[type](eventNames) : true;

const filterTypes = (permissions, types, eventNames) => {
  // return types.filter((type) => hasPermissions(type.permission, permissions))
  return types.filter(({ type }) => filterByExistEvents(type, eventNames));
};

export const TYPES_LIST = [
  {
    id: 0,
    name: unitsTypes.EVENTS_TYPE,
    type: unitsTypes.EVENTS_TYPE,
    permission: [staticPermissions.METAINF_READ],
  },
  {
    id: 1,
    name: unitsTypes.RETENTION_TYPE,
    type: unitsTypes.RETENTION_TYPE,
    permission: [
      staticPermissions.REGISTER_FIRST_START_EXECUTE,
      staticPermissions.DAU_EXECUTE,
      staticPermissions.WAU_EXECUTE,
      staticPermissions.MAU_EXECUTE,
    ],
  },
  {
    id: 2,
    name: 'RETENTION (COMPLEX)',
    type: unitsTypes.RETENTION_COMPLEX_TYPE,
    permission: [staticPermissions.METAINF_READ],
  },
  {
    id: 3,
    name: unitsTypes.USERS_TYPE,
    type: unitsTypes.USERS_TYPE,
    permission: [staticPermissions.METAINF_READ],
  },
  {
    id: 4,
    name: unitsTypes.REVENUE_TYPE,
    type: unitsTypes.REVENUE_TYPE,
    permission: [staticPermissions.PAYMENT_EXECUTE, staticPermissions.DAU_EXECUTE],
  },
  {
    id: 5,

    name: unitsTypes.FUNNELS_TYPE,
    type: unitsTypes.FUNNELS_TYPE,
    // TODO перепроверить пермишены
    permission: [staticPermissions.FUNNEL_READ, staticPermissions.FUNNEL_MANAGE],
  },
];

const UnitBuilderCreateHeader = ({
  onToggleTemplate,
  templates,
  unit,
  setUnitType,
  deleteUnit,
  changeSizeUnit,
  onChangeUnitName,
  paths,
  setMetaInfForRevenue,
  quantityUnits,
  events,
  permissions,
  showNotValidFields,
  funnels,
  startUpdateFunnel,
  dragOptions,
}) => {
  const [template, setTemplate] = useState({});

  const [isValid, setValid] = useState(true);

  useEffect(() => {
    if (showNotValidFields) {
      setValid(!!unit.name);
    }
  }, [unit.name, showNotValidFields]);

  const [filteredTemplates, setFilteredTemplates] = useState([]);

  useEffect(() => {
    const tempTemplates = templates.filter((t) => typesToTemplates[t.type] === unit.type);
    setFilteredTemplates(tempTemplates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.type]);

  const filteredTypes = useMemo(
    () => filterTypes(permissions, TYPES_LIST, events),
    [events, permissions]
  );

  const onChange = (name) => {
    onChangeUnitName(unit.id, name);
  };

  return (
    <StyledHeader {...dragOptions} className="unit-header">
      <div className="left-header">
        <Input
          placeholder="Enter item name"
          className={`item-name-input ${isValid ? '' : 'not-valid'}`}
          value={unit.name}
          onChange={(e) => onChange(e.target.value)}
        />
        <CustomSelect
          className="type-select"
          dropdownMatchSelectWidth={false}
          defaultValue={filteredTypes.find(({ type }) => type === unit.type)}
          placeholder="Type"
          values={filteredTypes}
          style={{
            fontWeight: 'normal',
            width: 'auto',
          }}
          onChange={(listId) => {
            const changedType = filteredTypes.find((item) => item.id === listId).type;
            setUnitType(unit.id, changedType);
            setTemplate({});
            if (changedType === unitsTypes.REVENUE_TYPE) {
              setMetaInfForRevenue(unit.id, paths);
            }
          }}
        />
        <CustomSelect
          className="templates-select"
          dropdownMatchSelectWidth
          defaultValue={template}
          values={unit.type === unitsTypes.FUNNELS_TYPE ? funnels : filteredTemplates}
          placeholder="Templates"
          style={{
            fontWeight: 'normal',
            width: '100px',
          }}
          onChange={(id) => {
            if (unit.type === unitsTypes.FUNNELS_TYPE) {
              const changedFunnel = funnels.find((f) => f.id === id);
              setTemplate(changedFunnel);
              startUpdateFunnel(changedFunnel, unit.id);
              return;
            }

            const resultTemplate = templates.find((item) => item.id === id);
            setTemplate(resultTemplate);
            setUnitType(unit.id, `${typesToTemplates[resultTemplate.type]}`);
            onToggleTemplate(resultTemplate, unit.id);
          }}
        />
      </div>
      <div className="right-header">
        {/* TODO если надо будет confirmation window использовать DeleteButton */}
        <StyledSwitch
          onChange={() => changeSizeUnit(unit.id)}
          checked={unit.isFullSize}
          checkedChildren="Full-size"
          unCheckedChildren="Size"
        />
        {quantityUnits > 1 && (
          <Button className="square-icon-button" onClick={() => deleteUnit(unit.id)}>
            <DeleteOutlined />
          </Button>
        )}
      </div>
    </StyledHeader>
  );
};

export default UnitBuilderCreateHeader;
